/**
 * CheckoutPage - Tunnel de commande en 3 étapes
 * Étape 1: Choix livraison/retrait magasin + Adresse
 * Étape 2: Date et créneau horaire
 * Étape 3: Mode de paiement
 *
 * Multi-tenant: tenant_id automatique via header x-tenant-id
 */

import { useState, useEffect, useRef } from 'react';
import { useTitrePage } from '../../hooks/useTitrePage';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useCustomer } from '../../contexts/CustomerContext';
import { useTenant } from '../../contexts/TenantContext';
import { useEcommerceCart } from '../../hooks/ecommerce/useEcommerceCart';
import { useLiveSession } from '../../hooks/live-shopping/useLiveSession';
import {
  ecommerceApi,
  type AvailabilityIssue,
  type DeliveryQuote,
  type OrderingState,
  type PickupDates,
  type ShopDeliveryConfig,
} from '../../api/ecommerce.api';
import { formatRemaining } from '../../utils/shopStock';
import { formatMomentBelge, isOrderableNow, formatJourReouverture, formatJourRetrait } from '../../utils/shopOrdering';
import { logger } from '../../utils/logger';
import type {
  DeliveryOption,
  PaymentMethod,
  CustomerAddress,
  Wallet
} from '../../types/ecommerce.types';
import VidangesAnnonce from '../../components/ecommerce/VidangesAnnonce';
import BacsAnnonce from '../../components/ecommerce/BacsAnnonce';

/** Repli si les horaires ne se chargent pas — miroir de DEFAULT_PICKUP_SLOTS côté serveur. */
const CRENEAUX_PAR_DEFAUT = ['10:00 - 12:00', '14:00 - 16:00', '16:00 - 18:00'];

/** 0 = dimanche … 6 = samedi, comme partout dans le code (JS getDay). */
const JOURS_COURTS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];

export default function CheckoutPage() {
  useTitrePage('Commander');
  const navigate = useNavigate();
  const { customer, isLoading: customerLoading } = useCustomer();
  const { tenant } = useTenant();
  // L'assistance téléphonique n'est pas ouverte au lancement : Twilio n'est pas
  // raccordé, un appel promis ici ne partirait jamais. Réglage plutôt que
  // suppression — le champ call_requested et l'écran de préparation restent en
  // place, il suffira de basculer le drapeau le jour où la ligne existe.
  const voiceAssistEnabled = tenant?.config?.ecommerce?.voiceAssistEnabled ?? false;
  // La livraison à domicile n'est pas ouverte : tant que ce réglage est à false,
  // le tunnel ne propose que le retrait en magasin (et ne demande donc pas
  // d'adresse). Rien n'est supprimé — le jour où la tournée existe, un clic suffit.
  const deliveryEnabled = tenant?.config?.ecommerce?.deliveryEnabled ?? true;
  const { items, getTotals, forceFullClear, removeItem } = useEcommerceCart();
  const { disconnect } = useLiveSession({
    role: 'customer',
    customerId: customer?.id,
  });
  const totals = getTotals();

  // Étapes
  const [currentStep, setCurrentStep] = useState(1);

  // Étape 1: Livraison/Retrait
  const [deliveryOption, setDeliveryOption] = useState<DeliveryOption>('pickup');
  const [addresses, setAddresses] = useState<CustomerAddress[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<string>('');
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [newAddress, setNewAddress] = useState({
    street: '',
    city: '',
    postal_code: '',
    country: 'BE',
    type: 'delivery' as const,
  });

  // Étape 2: Date et heure
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliverySlot, setDeliverySlot] = useState('');

  // Étape 3: Paiement
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('wallet');
  // Paiement au retrait : réservé aux clients que le magasin a autorisés
  // (fiche client du dashboard, 19/10/2026), et jamais en livraison. Le
  // serveur le revérifie : cacher le bouton ne suffit pas, ne pas l'avoir
  // non plus — un client non autorisé reçoit un refus.
  const auRetraitAutorise = !!customer?.paiement_au_retrait && deliveryOption !== 'delivery';
  useEffect(() => {
    if (paymentMethod === 'cash' && !auRetraitAutorise) setPaymentMethod('wallet');
  }, [paymentMethod, auRetraitAutorise]);
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [customerComment, setCustomerComment] = useState('');
  // Vidanges annoncées : { id de la fiche consigne → nombre }. Elles vivent dans
  // le panier (choisies dès le panier, effacées avec lui après la commande).
  const vidanges = useEcommerceCart((s) => s.vidanges);
  const setVidanges = useEcommerceCart((s) => s.setVidanges);
  // Bacs Folies vides rapportés : échangés un pour un au retrait (CGV art. 9)
  const bacs = useEcommerceCart((s) => s.bacs);
  const setBacs = useEcommerceCart((s) => s.setBacs);
  const vidangesAnnoncees = () =>
    Object.entries(vidanges)
      .filter(([, q]) => q > 0)
      .map(([product_id, quantity]) => ({ product_id, quantity }));

  // Appel personnalisé pendant la préparation
  const [callRequested, setCallRequested] = useState(false);

  // Ruptures détectées au moment de valider (le catalogue n'affiche plus les
  // quantités : c'est ici que le client apprend ce qui manque, et de combien).
  const [stockIssues, setStockIssues] = useState<AvailabilityIssue[]>([]);

  // Horaires de la boutique et dates de retrait — calculés par le serveur.
  const [ordering, setOrdering] = useState<OrderingState | null>(null);
  // Hors fenêtre, seul le frais est refusé : l'épicerie et les pains sur
  // commande passent. On ne bloque donc que si le panier en contient.
  const articlesBloques = items.filter(item => !isOrderableNow(item.product, ordering));
  const [pickupDates, setPickupDates] = useState<string[]>([]);
  const [pickupSlots, setPickupSlots] = useState<string[]>([]);
  const [pickupConstraints, setPickupConstraints] = useState<PickupDates['constraints']>([]);
  const [pickupRetards, setPickupRetards] = useState<NonNullable<PickupDates['retards']>>([]);
  const [pickupLoading, setPickupLoading] = useState(true);

  // Frais de livraison : plus rien n'est écrit en dur ici. Le tarif dépend de la
  // commune et du panier, et c'est le serveur qui tranche — la même fonction
  // annonce le prix et l'enregistre sur la commande.
  const [shopDelivery, setShopDelivery] = useState<ShopDeliveryConfig | null>(null);
  const [deliveryQuote, setDeliveryQuote] = useState<DeliveryQuote | null>(null);
  const [deliveryQuoteLoading, setDeliveryQuoteLoading] = useState(false);

  const selectedAddress = addresses.find(a => a.id === selectedAddressId) || null;
  const deliveryFeeCents = deliveryOption === 'delivery' ? deliveryQuote?.fee_cents ?? 0 : 0;
  /** Ce qui sera réellement débité : c'est ce total que le serveur vérifie. */
  /** Total courant, lisible depuis une boucle lancée au montage (frais de
      livraison compris : ils arrivent après le premier rendu). */
  const totalAttenduRef = useRef<number>(0);
  const totalAvecLivraisonCents = totals.total_ttc_cents + deliveryFeeCents;
  totalAttenduRef.current = totalAvecLivraisonCents;
  /** Adresse hors zone ou panier sous le minimum : le serveur refuserait. */
  const livraisonRefusee =
    deliveryOption === 'delivery' && deliveryQuote != null && !deliveryQuote.deliverable;
  /**
   * Devis pas encore rendu (en cours, ou en échec réseau) : on ne laisse PAS
   * valider. Sans cette garde, la page affichait le panier sans frais de
   * livraison — total minoré de cinq euros — puis Stripe réclamait le vrai
   * montant, ou le paiement en FOLIES échouait sur « solde insuffisant » sans
   * proposer de recharger, puisque l'écran croyait le solde suffisant.
   */
  const devisLivraisonManquant =
    deliveryOption === 'delivery' && !!selectedAddressId && (deliveryQuoteLoading || deliveryQuote == null);

  // Acceptation CGV (obligatoire)
  const [cgvAccepted, setCgvAccepted] = useState(false);

  // Code promo
  const [promoCode, setPromoCode] = useState('');
  const [promoDiscount, setPromoDiscount] = useState<{ discount_cents: number; promo_code_id: string; type: string; description?: string } | null>(null);
  const [promoError, setPromoError] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);

  // États
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Montant minimum de commande (Paramètres › E-commerce ; 0 = aucun) — même
  // réglage que le serveur, pour le dire AVANT le clic plutôt qu'en erreur après.
  const minOrderCents = Math.round(Number(tenant?.config?.ecommerce?.minOrderAmount ?? 0) * 100);

  // Retour de Stripe après « compléter mon solde » (?deposit=success|cancelled) :
  // le panier est en localStorage, il a survécu à l'aller-retour ; on revient
  // directement à l'étape paiement, FOLIES présélectionnées.
  const [searchParams, setSearchParams] = useSearchParams();
  const [depositNotice, setDepositNotice] = useState<string | null>(null);
  const [topUpLoading, setTopUpLoading] = useState(false);

  /**
   * Au retour d'une recharge, le panier peut très bien être vide : Stripe rouvre
   * parfois un autre onglet (navigateur interne de Facebook), et c'est
   * justement le cas que ce chantier couvre. Lu à la première image, AVANT que
   * l'effet ci-dessous ne nettoie l'URL, pour que la garde « panier vide » ne
   * renvoie pas le client au catalogue pendant qu'on enregistre sa commande.
   */
  const parametresAuChargement =
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const suivreBrouillonRef = useRef<boolean>(parametresAuChargement.has('draft'));
  /**
   * Le retour de Stripe est lu UNE fois, à la première image, et gardé dans des
   * refs. L'effet nettoie l'URL, ce qui faisait changer sa dépendance, donc
   * React rejouait son nettoyage : le suivi s'annulait lui-même après un seul
   * relevé et le client restait bloqué sur « Enregistrement de votre commande… ».
   */
  const retourRechargeRef = useRef<string | null>(parametresAuChargement.get('deposit'));
  const brouillonIdRef = useRef<string | null>(parametresAuChargement.get('draft'));
  /**
   * Tant que le serveur enregistre la commande promise par la recharge, le
   * bouton « Confirmer » est neutralisé : sinon un client dont le panier a
   * survécu au retour pourrait commander une SECONDE fois ce que le webhook
   * est déjà en train d'enregistrer.
   */
  const [brouillonEnCours, setBrouillonEnCours] = useState<boolean>(suivreBrouillonRef.current);

  useEffect(() => {
    const retour = retourRechargeRef.current;
    if (!retour) return;
    const draftId = brouillonIdRef.current;
    searchParams.delete('deposit');
    searchParams.delete('session_id');
    searchParams.delete('draft');
    setSearchParams(searchParams, { replace: true });

    // Choix faits avant le départ vers Stripe (date, adresse, commentaire…) :
    // la page a été rechargée, on les remet en place. Vaut aussi sur le chemin
    // du brouillon : si l'enregistrement échoue, le client doit retrouver son
    // tunnel tel qu'il l'avait rempli, pas une page vierge.
    try {
      const saved = sessionStorage.getItem('checkout_resume');
      if (saved) {
        const r = JSON.parse(saved);
        if (r.deliveryOption) setDeliveryOption(r.deliveryOption);
        if (r.selectedAddressId) setSelectedAddressId(r.selectedAddressId);
        if (r.deliveryDate) setDeliveryDate(r.deliveryDate);
        if (r.deliverySlot) setDeliverySlot(r.deliverySlot);
        if (r.customerComment) setCustomerComment(r.customerComment);
        if (r.callRequested) setCallRequested(true);
        sessionStorage.removeItem('checkout_resume');
      }
    } catch (err) {
      logger.error('Restauration du checkout après recharge:', err);
    }
    setCurrentStep(3);
    setPaymentMethod('wallet');

    // Recharge aboutie avec un panier mis de côté : la commande est créée par
    // le serveur au moment du crédit. On ne reconstruit RIEN ici — le panier a
    // pu ne pas survivre à l'aller-retour (autre onglet, navigateur interne
    // d'une application) — on demande simplement où en est le brouillon.
    if (draftId && retour === 'success') {
      setDepositNotice('Paiement reçu. Enregistrement de votre commande…');
      let essais = 0;
      let arrete = false;
      const suivre = async () => {
        if (arrete) return;
        try {
          const brouillon = await ecommerceApi.orders.getDraft(draftId);
          if (brouillon.status === 'consumed' && brouillon.order_id) {
            const retirees = brouillon.adjustments?.retirees || [];
            const reduites = brouillon.adjustments?.reduites || [];
            if (retirees.length || reduites.length) {
              // Le magasin est prévenu par email ; le client le voit ici.
              sessionStorage.setItem(
                'order_stock_adjustments',
                JSON.stringify({ retirees, reduites }),
              );
            }
            navigate(`/shop/order-confirmation/${brouillon.order_id}`, { replace: true });
            setTimeout(() => {
              forceFullClear();
              ecommerceApi.cart.clear().catch(() => {});
            }, 100);
            return;
          }
          if (brouillon.status === 'empty') {
            // Le crédit, lui, est acquis : sans relecture, l'écran afficherait
            // encore l'ancien solde et reproposerait de recharger.
            loadWallet();
            setBrouillonEnCours(false);
            setDepositNotice(null);
            setError(
              'Votre paiement est bien arrivé sur votre compte FOLIES, mais les articles ' +
                'de votre panier ne sont plus disponibles. Le magasin est prévenu et vous ' +
                'recontacte — votre solde est intact.',
            );
            return;
          }
          if (brouillon.status === 'failed') {
            loadWallet();
            setBrouillonEnCours(false);
            setDepositNotice(null);
            setError(
              'Votre paiement est bien arrivé sur votre compte FOLIES, mais votre commande ' +
                "n'a pas pu être enregistrée. Le magasin est prévenu et vous recontacte — " +
                'votre solde est intact.',
            );
            return;
          }
        } catch (err) {
          logger.error('Suivi du brouillon de commande:', err);
        }
        essais += 1;
        if (essais > 20) {
          setDepositNotice(
            'Votre paiement est enregistré. Votre commande apparaîtra dans « Mes commandes » ' +
              "d'ici quelques instants.",
          );
          return;
        }
        setTimeout(suivre, 1500);
      };
      suivre();
      return () => {
        arrete = true;
      };
    }

    if (retour === 'cancelled') {
      setDepositNotice('Recharge annulée : votre solde est inchangé.');
      return;
    }
    setDepositNotice('Merci ! Vérification de votre solde en cours…');
    // Le portefeuille est crédité par le webhook Stripe, parfois une ou deux
    // secondes après le retour : on relit le solde jusqu'à le voir bouger.
    let tries = 0;
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      try {
        const data = await ecommerceApi.wallet.getBalance();
        setWallet(data);
        const attendu = totalAttenduRef.current;
        if (data.balance_cents >= attendu || tries >= 8) {
          setDepositNotice(
            data.balance_cents >= attendu
              ? `Solde rechargé : ${(data.balance_cents / 100).toFixed(2)} € disponibles. Vous pouvez confirmer votre commande.`
              : 'Recharge en cours de traitement — le solde se mettra à jour dans quelques instants.',
          );
          return;
        }
      } catch (err) {
        logger.error('Erreur relecture wallet:', err);
      }
      tries += 1;
      setTimeout(poll, 1500);
    };
    poll();
    return () => {
      stopped = true;
    };
    // Une seule exécution, au montage : les paramètres de retour sont lus dans
    // des refs, l'effet ne doit pas se rejouer quand l'URL est nettoyée.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Complément EXACT au centime (1 € minimum, accepté par l'API pour un retour
      vers le checkout) : le client ne recharge que ce qui manque. */
  const missingCents = wallet ? Math.max(0, Math.round(totalAvecLivraisonCents - wallet.balance_cents)) : 0;
  const topUpCents = Math.max(100, missingCents);
  const handleTopUpMissing = async () => {
    // Une commande est déjà en train d'être enregistrée par le serveur :
    // repartir vers Stripe déposerait un second brouillon, donc un second
    // paiement et une seconde commande.
    if (brouillonEnCours) return;
    if (!wallet) return;
    if (missingCents <= 0) return;
    if (!validateStep1() || !validateStep2()) return;
    if (!cgvAccepted) {
      setError('Veuillez accepter les CGV et la Politique de confidentialite avant de recharger.');
      return;
    }
    let amountCents = topUpCents;
    try {
      setTopUpLoading(true);

      const lignes = construireLignesCommande();

      // La rupture se dit AVANT de prendre l'argent — c'est déjà la règle du
      // tunnel normal, et elle vaut d'autant plus ici : la commande sera créée
      // par le serveur, sans le client devant l'écran, et un article manquant
      // serait simplement réduit sur la commande. Il a le droit de le savoir
      // pendant qu'il peut encore ajuster son panier (essai du 07/09 : trois
      // ails noirs commandés, deux en stock, découverte après paiement).
      const disponibilite = await ecommerceApi.orders.checkAvailability(
        lignes.map(l => ({
          product_id: l.product_id,
          product_name: l.product_name,
          quantity: l.quantity,
          quantity_unit: l.quantity_unit,
          is_weighted: l.is_weighted,
          lot_id: (l as any).lot_id,
        })),
      );
      if (!disponibilite.ok) {
        setStockIssues(disponibilite.issues);
        setError(null);
        setTopUpLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      setStockIssues([]);

      // Le panier part sur le serveur AVANT le départ vers Stripe : c'est le
      // webhook qui créera la commande une fois le compte crédité, que le
      // client revienne ou non — et quel que soit l'onglet dans lequel Stripe
      // le renvoie. Incident du 05/09/2026 : 127,05 € encaissés, panier perdu
      // au retour dans le navigateur interne de Facebook, aucune commande.
      const brouillon = await ecommerceApi.orders.createDraft({
        delivery_option: deliveryOption === 'delivery' ? 'delivery' : 'prepaid_pickup',
        address_id: deliveryOption === 'delivery' ? selectedAddressId : undefined,
        delivery_date: deliveryDate || undefined,
        delivery_slot: deliverySlot || undefined,
        payment_method: 'wallet',
        customer_comment: customerComment.trim() || undefined,
        call_requested: voiceAssistEnabled && callRequested,
        vidanges: deliveryOption === 'delivery' ? undefined : vidangesAnnoncees(),
        bacs_rapportes: deliveryOption === 'delivery' || bacs <= 0 ? undefined : bacs,
        lines: lignes,
      } as any);

      // Le montant qui compte est celui que le SERVEUR facturera (consignes
      // comprises, calculées sur les fiches du jour) : un panier gardé depuis
      // plusieurs jours peut porter un produit sans son montant de consigne,
      // et une recharge trop courte ferait refuser la commande APRÈS paiement.
      const manqueServeur = Math.round(brouillon.expected_total_cents + deliveryFeeCents - wallet.balance_cents);
      // Le SERVEUR décide, dans les deux sens : avant (30/09/2026) on prenait le
      // plus grand des deux montants, et un total de page trop haut faisait payer
      // plus que la commande (97,56 € encaissés pour 73,65 € commandés).
      amountCents = Math.max(100, manqueServeur);

      const response = await ecommerceApi.stripe.createCheckoutSession({
        amount_cents: amountCents,
        return_path: '/shop/checkout',
        draft_id: brouillon.draft_id,
      });
      if (response.url) {
        // La page va être rechargée au retour : on garde les choix du client.
        try {
          sessionStorage.setItem(
            'checkout_resume',
            JSON.stringify({ deliveryOption, selectedAddressId, deliveryDate, deliverySlot, customerComment, callRequested }),
          );
        } catch {
          /* stockage indisponible : le client re-choisira sa date */
        }
        window.location.href = response.url;
      } else {
        setError('Impossible de démarrer la recharge.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Impossible de démarrer la recharge.');
    } finally {
      setTopUpLoading(false);
    }
  };

  // Livraison fermée : ramener le choix sur le retrait, même si un ancien état
  // (retour arrière, panier repris) portait encore « livraison ».
  useEffect(() => {
    if (!deliveryEnabled) setDeliveryOption('pickup');
  }, [deliveryEnabled]);

  // Vérifications initiales — PAS avant que le contexte client ait fini de
  // relire la session : au chargement direct de la page (refresh, retour de
  // Stripe après une recharge) `customer` est null pendant quelques
  // centaines de ms → le checkout renvoyait vers la connexion, qui, voyant
  // un client connecté, renvoyait au catalogue. Panier intact, client perdu.
  useEffect(() => {
    if (customerLoading) return;
    if (!customer) {
      navigate('/shop/login');
      return;
    }

    // Panier vide MAIS une commande en cours d'enregistrement : on reste, le
    // suivi du brouillon emmènera le client sur sa confirmation.
    if (items.length === 0 && !suivreBrouillonRef.current) {
      navigate('/shop');
      return;
    }

    loadAddresses();
    loadWallet();
  }, [customer, customerLoading, items.length]);

  // Horaires + dates de retrait : rechargés quand le panier change, puisque les
  // dates possibles dépendent des produits (un pain n'est pas retirable
  // n'importe quel jour).
  useEffect(() => {
    let annule = false;
    const charger = async () => {
      setPickupLoading(true);
      try {
        const productIds = items.map(i => i.product.id).filter(Boolean);
        const [config, pickup] = await Promise.all([
          ecommerceApi.shop.getConfig(),
          ecommerceApi.shop.getPickupDates(productIds),
        ]);
        if (annule) return;
        setOrdering(config.ordering);
        setShopDelivery(config.delivery ?? null);
        setPickupDates(pickup.dates);
        setPickupSlots(pickup.slots);
        setPickupConstraints(pickup.constraints);
        setPickupRetards(pickup.retards ?? []);
        // Une date choisie puis devenue impossible (heure limite franchie
        // pendant que le client remplissait son panier) ne doit pas rester.
        setDeliveryDate(prev => (prev && pickup.dates.includes(prev) ? prev : ''));
        setDeliverySlot(prev => (prev && pickup.slots.includes(prev) ? prev : ''));
      } catch (err) {
        logger.error('Erreur chargement horaires boutique:', err);
        if (!annule) {
          // Échec de l'appel : pas de date proposée (le client verra le message
          // « aucune date »), mais on garde des créneaux par défaut — sans quoi
          // l'étape 2 deviendrait invalidable et le tunnel serait bloqué net.
          // Le serveur revalidera de toute façon à la commande.
          setPickupDates([]);
          setPickupSlots(CRENEAUX_PAR_DEFAUT);
        }
      } finally {
        if (!annule) setPickupLoading(false);
      }
    };
    charger();
    return () => {
      annule = true;
    };
  }, [items.map(i => `${i.product.id}:${i.quantity}`).join('|')]);

  // Le devis est refait dès que la commune ou le panier change : un tarif de
  // zone et un franco ne veulent rien dire hors de CE panier à CETTE adresse.
  useEffect(() => {
    if (!deliveryEnabled || deliveryOption !== 'delivery' || !selectedAddress?.postal_code) {
      setDeliveryQuote(null);
      return;
    }

    let annule = false;
    setDeliveryQuoteLoading(true);
    ecommerceApi.shop
      .getDeliveryQuote(selectedAddress.postal_code, totals.total_ttc_cents)
      .then(devis => {
        if (!annule) setDeliveryQuote(devis);
      })
      .catch(err => {
        logger.error('Erreur devis livraison:', err);
        // Un devis qui n'a pas abouti ne devient pas un tarif inventé : on
        // n'affiche aucun montant, et le serveur reste l'arbitre à la commande.
        if (!annule) setDeliveryQuote(null);
      })
      .finally(() => {
        if (!annule) setDeliveryQuoteLoading(false);
      });

    return () => {
      annule = true;
    };
  }, [deliveryEnabled, deliveryOption, selectedAddress?.postal_code, totals.total_ttc_cents]);

  /**
   * Ce qu'on peut annoncer sur la carte « Livraison » AVANT de connaître la
   * commune : les tarifs des zones publiés par la boutique, sans les codes
   * postaux. Une fois l'adresse choisie, le devis prend le relais.
   */
  const libelleFraisLivraison = (): string => {
    const tarifs = Array.from(new Set((shopDelivery?.zones ?? []).map(z => z.fee_cents))).sort(
      (a, b) => a - b,
    );
    if (tarifs.length === 0) return 'Frais selon votre commune';
    if (tarifs.length === 1) {
      return tarifs[0] === 0
        ? 'Livraison offerte'
        : `Frais de livraison : ${(tarifs[0] / 100).toFixed(2)} €`;
    }
    return `Frais de livraison : de ${(tarifs[0] / 100).toFixed(2)} € à ${(tarifs[tarifs.length - 1] / 100).toFixed(2)} €`;
  };

  const loadAddresses = async () => {
    if (!customer) return;
    try {
      const data = await ecommerceApi.addresses.getAddresses();
      setAddresses(data);
      const defaultAddr = data.find(a => a.is_default);
      if (defaultAddr) setSelectedAddressId(defaultAddr.id);
    } catch (err) {
      logger.error('Erreur chargement adresses:', err);
    }
  };

  const loadWallet = async () => {
    try {
      const data = await ecommerceApi.wallet.getBalance();
      setWallet(data);
    } catch (err) {
      logger.error('Erreur chargement wallet:', err);
    }
  };

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoError('');
    try {
      const result = await ecommerceApi.promo.validate(promoCode.trim(), totals.total_ttc_cents);
      setPromoDiscount(result);
    } catch (err: any) {
      setPromoError(err.response?.data?.message || 'Code invalide');
      setPromoDiscount(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const handleCreateAddress = async () => {
    if (!customer) return;
    try {
      setLoading(true);
      const created = await ecommerceApi.addresses.createAddress(newAddress);
      setAddresses([...addresses, created]);
      setSelectedAddressId(created.id);
      setShowAddressForm(false);
      setNewAddress({ street: '', city: '', postal_code: '', country: 'BE', type: 'delivery' });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur création adresse');
    } finally {
      setLoading(false);
    }
  };

  const validateStep1 = () => {
    if (deliveryOption === 'delivery' && !selectedAddressId) {
      setError('Veuillez sélectionner une adresse de livraison');
      return false;
    }
    // Hors zone ou sous le minimum : le dire ici, pas en erreur après paiement.
    if (livraisonRefusee) {
      setError(deliveryQuote?.reason || 'Cette adresse ne peut pas être livrée.');
      return false;
    }
    if (devisLivraisonManquant) {
      setError(
        deliveryQuoteLoading
          ? 'Calcul des frais de livraison en cours…'
          : "Les frais de livraison n'ont pas pu être calculés. Vérifiez votre connexion et réessayez.",
      );
      return false;
    }
    setError(null);
    return true;
  };

  const validateStep2 = () => {
    if (!deliveryDate) {
      setError('Veuillez sélectionner une date');
      return false;
    }
    // Créneau seulement s'il y en a de configurés. Aujourd'hui la boutique n'en
    // publie pas : le client choisit un JOUR et vient quand il a reçu le message
    // lui disant que sa commande est prête.
    if (timeSlots.length > 0 && !deliverySlot) {
      setError('Veuillez sélectionner un créneau horaire');
      return false;
    }
    setError(null);
    return true;
  };

  const validateStep3 = () => {
    // Les frais de livraison font partie de ce qui sera débité : le serveur
    // vérifie le solde sur le total AVEC livraison, l'écran doit faire pareil.
    if (paymentMethod === 'wallet' && wallet && wallet.balance_cents < totalAvecLivraisonCents) {
      setError(`Solde insuffisant. Votre solde: ${(wallet.balance_cents / 100).toFixed(2)} €`);
      return false;
    }
    setError(null);
    return true;
  };

  const handleNextStep = () => {
    if (currentStep === 1 && validateStep1()) {
      setCurrentStep(2);
    } else if (currentStep === 2 && validateStep2()) {
      setCurrentStep(3);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError(null);
    }
  };

  /**
   * Les lignes du panier au format attendu par le serveur.
   *
   * Construites UNE fois et réutilisées : la vérification de disponibilité, la
   * commande et le brouillon déposé avant une recharge doivent porter sur
   * exactement la même donnée, sans quoi on validerait un panier et on en
   * commanderait un autre.
   */
  const construireLignesCommande = () => items.map(item => {
      const isWeighted = item.product.is_weighted === true;
      const isPieceOrder = item.quantity_unit === 'pieces';

      // Déterminer la quantité et l'unité pour le backend
      let finalQuantity: number;
      let finalTotalPriceCents: number;

      if (isPieceOrder && item.piece_count) {
        // Commande à la pièce : quantity = nombre de pièces, × le nombre de lots
        // choisi avec « + » dans le panier. Avant (30/09/2026), seul UN lot
        // partait : 2 × « 5 tomates » affichait et faisait payer 10 tomates, la
        // commande n'en contenait que 5 (commandes Verzele et Mattei).
        const lots = Math.max(1, item.quantity || 1);
        finalQuantity = item.piece_count * lots;
        // Prix basé sur le poids estimé (weight_grams = poids d'UN lot)
        finalTotalPriceCents = item.weight_grams
          ? Math.round((item.unit_price_cents / 1000) * item.weight_grams * lots)
          : item.total_price_cents;
      } else if (isWeighted && item.weight_grams) {
        // Commande au poids : normaliser en grammes totaux
        // Client a ajouté X unités de Y grammes → convertir en grammes totaux
        finalQuantity = item.quantity * item.weight_grams;
        finalTotalPriceCents = Math.round((item.unit_price_cents / 1000) * finalQuantity);
      } else {
        // Produit normal (unité) ou vendeur
        finalQuantity = item.quantity;
        finalTotalPriceCents = isWeighted
          ? Math.round((item.unit_price_cents / 1000) * finalQuantity)
          : item.total_price_cents;
      }

      return {
        product_id: item.product.id,
        product_name: item.product.name,
        quantity: finalQuantity,
        price_cents: item.unit_price_cents,
        total_price_cents: finalTotalPriceCents,
        is_weighted: isWeighted,
        // Poids estimé TOTAL pour une commande à la pièce (lots compris).
        weight_grams:
          (isPieceOrder && item.piece_count && item.weight_grams
            ? item.weight_grams * Math.max(1, item.quantity || 1)
            : item.weight_grams) || undefined,
        quantity_unit: item.quantity_unit || (isWeighted ? 'grams' : undefined),
        // Offre anti-gaspi choisie : le serveur vérifie le lot et recalcule le prix.
        ...(item.lot ? { lot_id: item.lot.lot_id } : {}),
        // Pain « coupé » : le serveur ne garde que ce que la fiche permet.
        ...(item.prep_options?.length ? { prep_options: item.prep_options } : {}),
        vat_rate: 6, // TVA alimentaire par défaut
        // Métadonnées Live Shopping
        prepared: item.prepared || false,
        added_by: item.addedBy || 'customer',
        added_at: item.addedAt || new Date().toISOString(),
      };
    });

  const handleConfirmOrder = async () => {
    // Une commande est déjà en train d'être enregistrée par le serveur suite à
    // la recharge : confirmer ici en créerait une seconde.
    if (brouillonEnCours) return;
    if (!validateStep3()) return;
    if (!customer) return;

    if (!cgvAccepted) {
      setError('Veuillez accepter les CGV et la Politique de confidentialite pour continuer.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Déterminer le delivery_option selon le choix de livraison et paiement
      let finalDeliveryOption: 'delivery' | 'pickup' | 'prepaid_pickup';

      if (deliveryOption === 'delivery') {
        finalDeliveryOption = 'delivery';
      } else {
        // Tout retrait est prépayé : le paiement sur place n'existe plus (CGV
        // art. 6). Une commande abandonnée au moment de payer reste `pending` et
        // sera annulée par le cron des commandes expirées — elle n'arrive donc
        // jamais au comptoir en se faisant passer pour payée.
        // Exception : le client autorisé à payer au retrait — sa commande se
        // règle au comptoir (`pickup`), comme une précommande de pain.
        finalDeliveryOption = paymentMethod === 'cash' ? 'pickup' : 'prepaid_pickup';
      }

      logger.log('[CheckoutPage] Création commande:', {
        deliveryOption,      // valeur du state (sélection utilisateur)
        paymentMethod,       // mode de paiement choisi
        finalDeliveryOption, // valeur finale calculée
      });

      // Récupérer le numéro de commande pré-réservé depuis Live Shopping (si présent)
      const reservedOrderNumber = sessionStorage.getItem('live_shopping_order_number') || undefined;
      const liveSessionId = sessionStorage.getItem('live_shopping_session_id') || undefined;

      const orderLines = construireLignesCommande();

      // Le stock a pu partir en magasin depuis que le panier a été rempli. On le
      // dit AVANT de prendre l'argent, et on nomme chaque article en défaut :
      // le client ne voit plus les quantités au catalogue, il n'a aucun autre
      // moyen de le savoir. (Avertissement, pas réservation : l'arbitre reste
      // deductStock à la création de la commande.)
      const availability = await ecommerceApi.orders.checkAvailability(
        orderLines.map(l => ({
          product_id: l.product_id,
          product_name: l.product_name,
          quantity: l.quantity,
          quantity_unit: l.quantity_unit,
          is_weighted: l.is_weighted,
          lot_id: (l as any).lot_id,
        })),
      );

      if (!availability.ok) {
        setStockIssues(availability.issues);
        setError(null);
        setLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      setStockIssues([]);

      const orderDto = {
        delivery_option: finalDeliveryOption,
        address_id: deliveryOption === 'delivery' ? selectedAddressId : undefined,
        delivery_date: deliveryDate || undefined,
        delivery_slot: deliverySlot || undefined,
        payment_method: paymentMethod,
        customer_comment: customerComment.trim() || undefined,
        call_requested: voiceAssistEnabled && callRequested,
        promo_code_id: promoDiscount?.promo_code_id || undefined,
        promo_discount_cents: promoDiscount?.discount_cents || undefined,
        reserved_order_number: reservedOrderNumber, // Numéro pré-réservé depuis Live Shopping
        live_session_id: liveSessionId, // ID de la session Live Shopping
        vidanges: deliveryOption === 'delivery' ? undefined : vidangesAnnoncees(),
        bacs_rapportes: deliveryOption === 'delivery' || bacs <= 0 ? undefined : bacs,
        lines: orderLines,
      };

      if (reservedOrderNumber) {
        logger.log('[CheckoutPage] Using reserved order number from Live Shopping:', reservedOrderNumber);
      }

      const order = await ecommerceApi.orders.createOrder(orderDto);

      // For card payments, redirect to Stripe Checkout
      if (paymentMethod === 'card' && order.id) {
        try {
          const totalAmountCents = Math.round(totalAvecLivraisonCents);
          const stripeSession = await ecommerceApi.stripe.createOrderPayment({
            order_id: order.id,
            amount_cents: totalAmountCents,
          });

          if (stripeSession.url) {
            // Clean up before redirect
            sessionStorage.removeItem('live_shopping_order_number');
            sessionStorage.removeItem('live_shopping_session_id');

            if (liveSessionId) {
              disconnect();
            }

            forceFullClear();
            ecommerceApi.cart.clear().catch(() => {});
            window.location.href = stripeSession.url;
            return;
          }
        } catch (stripeErr) {
          logger.error('Stripe payment error:', stripeErr);
          // Order was created, navigate to confirmation (payment pending)
        }
      }

      // Si la commande vient d'un Live Shopping, fermer proprement la session
      if (liveSessionId) {
        logger.log('[CheckoutPage] Closing live shopping session after successful order:', liveSessionId);
        // Fermer la connexion WebSocket/WebRTC côté client
        // Cela informe le vendeur que le client a finalisé sa commande
        disconnect();
      }

      // Succès ! Important: navigate AVANT forceFullClear pour éviter le redirect du useEffect
      navigate(`/shop/order-confirmation/${order.id}`, { replace: true });

      // Nettoyer les données Live Shopping du sessionStorage
      sessionStorage.removeItem('live_shopping_order_number');
      sessionStorage.removeItem('live_shopping_session_id');

      // Vider complètement le panier après la navigation (y compris articles du vendeur)
      setTimeout(() => {
        forceFullClear();
        // Nettoyer aussi le panier côté serveur (DB)
        ecommerceApi.cart.clear().catch(() => {});
      }, 100);
    } catch (err: any) {
      logger.error('Erreur création commande:', err);
      const data = err.response?.data;

      // Course perdue : quelqu'un a acheté l'article entre la vérification et la
      // validation. Même présentation que la vérification — le client comprend
      // ce qui manque au lieu de lire un message technique.
      if (data?.error_type === 'INSUFFICIENT_STOCK') {
        // L'API renvoie des quantités en unité de STOCK (kg ou pièces) : on
        // reprend la nature du produit dans le panier pour les formater juste.
        const inCart = items.find(i => i.product.id === data.product_id)?.product;
        setStockIssues([
          {
            product_id: data.product_id,
            product_name: data.product_name || inCart?.name || 'Article',
            requested: Number(data.requested) || 0,
            available: Number(data.available) || 0,
            is_weighted: inCart?.is_weighted === true,
            unit: inCart?.unit || 'unité',
            reason: 'INSUFFICIENT_STOCK',
          },
        ]);
        setError(null);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setError(data?.message || 'Erreur lors de la création de la commande');
      }
    } finally {
      setLoading(false);
    }
  };

  // Créneaux disponibles
  // Dates et créneaux viennent du SERVEUR (POST /shop-config/pickup-dates) :
  // jours d'ouverture du magasin, fermetures, et surtout ce que le moteur de
  // précommande autorise vraiment (jour de fabrication, livraison du
  // fournisseur, heure limite déjà passée). L'ancien calcul local faisait
  // l'UNION des jours des produits du panier au lieu de leur INTERSECTION : un
  // panier « pain du vendredi + croissant du samedi » proposait les deux jours,
  // et l'un des deux articles manquait au retrait.
  const timeSlots = pickupSlots;
  const availableDates = pickupDates;
  const hasPreorderItems = items.some(item => item.product.preorder_enabled);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <button
            onClick={() => navigate('/shop')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <svg className="w-6 h-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Retour à la boutique
          </button>

          <h1 className="text-4xl font-bold text-gray-900 mb-2">Finaliser ma commande</h1>
          <p className="text-gray-600">
            Total à payer : <span className="text-3xl font-bold text-green-700">{totals.total_ttc_euros} €</span>
          </p>
        </div>

        {/* Indicateur d'étapes */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            {[1, 2, 3].map(step => (
              <div key={step} className="flex items-center flex-1">
                <div className="flex items-center flex-col">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl transition-all ${
                      step === currentStep
                        ? 'bg-green-600 text-white scale-110'
                        : step < currentStep
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {step < currentStep ? '✓' : step}
                  </div>
                  <div className="mt-2 text-sm font-medium text-gray-700">
                    {step === 1 && 'Livraison'}
                    {step === 2 && 'Date & Heure'}
                    {step === 3 && 'Paiement'}
                  </div>
                </div>
                {step < 3 && (
                  <div
                    className={`flex-1 h-1 mx-4 transition-colors ${
                      step < currentStep ? 'bg-green-500' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Message d'erreur */}
        {/* Hors fenêtre : on ne barre le passage que pour les articles
            réellement concernés (le frais), et on les nomme. */}
        {articlesBloques.length > 0 && (
          <div className="bg-[#4a5c3a]/10 border-2 border-[#4a5c3a]/40 rounded-xl p-5 mb-6">
            <p className="font-bold text-[#3c4a2f] text-lg mb-1">
              Certains articles ne sont pas commandables aujourd'hui
            </p>
            <p className="text-[#4a5c3a]">
              {articlesBloques.map(i => i.product.name).join(', ')} — commandable
              {articlesBloques.length > 1 ? 's' : ''} {formatJourReouverture(ordering?.next_open_at)}
              {ordering?.next_open_at ? ` (${formatMomentBelge(ordering.next_open_at)})` : ''}.
            </p>
            <p className="text-[#4a5c3a] mt-2">
              Le reste de votre panier est commandable : retirez
              {articlesBloques.length > 1 ? ' ces articles' : ' cet article'} pour passer commande.
            </p>
            <Link
              to="/shop/cart"
              className="inline-block mt-3 bg-[#4a5c3a] hover:bg-[#3c4a2f] text-white font-bold px-5 py-2.5 rounded-lg"
            >
              Modifier mon panier
            </Link>
          </div>
        )}

        {ordering && !ordering.open && articlesBloques.length === 0 && (
          <div className="bg-[#f0e9d8] text-[#4a4530] rounded-xl px-4 py-3 mb-6 text-center">
            Les commandes du frais sont fermées jusqu'à {ordering.next_open_at ? formatMomentBelge(ordering.next_open_at) : 'la réouverture'},
            mais votre panier ne contient que des articles disponibles : vous pouvez valider.
          </div>
        )}

        {ordering?.open && ordering.closes_at && (
          <div className="text-center text-[#6b6550] mb-4">
            Commandes ouvertes jusqu'au {formatMomentBelge(ordering.closes_at)}
          </div>
        )}

        {/* Rupture constatée au moment de valider. Le catalogue n'affiche plus
            les quantités : c'est le seul endroit où le client peut l'apprendre,
            donc on nomme chaque article et ce qu'il en reste. */}
        {stockIssues.length > 0 && (
          <div className="bg-[#b45f2e]/10 border-2 border-[#b45f2e]/40 rounded-xl p-5 mb-6">
            <p className="font-bold text-[#8f4a22] text-lg mb-2">
              Nous n'avons plus assez de stock pour votre commande
            </p>
            <p className="text-[#5c4633] mb-3">
              Un client est passé avant vous en magasin. Ajustez votre panier, puis revenez valider :
            </p>
            <ul className="space-y-2 mb-4">
              {stockIssues.map(issue => (
                <li key={issue.product_id} className="text-[#3a3324]">
                  <span className="font-bold">{issue.product_name}</span>
                  {issue.reason === 'UNKNOWN_PRODUCT' ? (
                    <span> — ce produit n'est plus au catalogue</span>
                  ) : issue.available <= 0 ? (
                    <span> — épuisé</span>
                  ) : (
                    <span>
                      {' '}— vous en demandez {formatRemaining(issue.requested, issue)}, il n'en reste que{' '}
                      <span className="font-bold">{formatRemaining(issue.available, issue)}</span>
                    </span>
                  )}
                </li>
              ))}
            </ul>
            <Link
              to="/shop/cart"
              className="inline-block bg-[#b45f2e] hover:bg-[#9d5227] text-white font-bold px-5 py-2.5 rounded-lg"
            >
              Modifier mon panier
            </Link>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4 mb-6">
            <p className="text-red-700 font-medium">{error}</p>
          </div>
        )}

        {/* Rendu HORS des étapes : au retour d'une recharge avec panier mis de
            côté, l'écran n'est pas forcément à l'étape 3, et le client doit
            voir « paiement reçu, enregistrement en cours » où qu'il soit. */}
        {depositNotice && (
          <div className="mb-6 rounded-xl border-2 border-green-300 bg-green-50 px-4 py-3 text-green-800 flex items-start justify-between gap-3">
            <div>
              <span className="font-medium">{depositNotice}</span>
              {/* Le suivi peut expirer avant le webhook : on ne laisse pas le
                  client sur une page sans issue, ses commandes sont à un clic. */}
              {brouillonEnCours && (
                <div className="mt-1">
                  <Link to="/shop/orders" className="underline font-medium">
                    Voir mes commandes
                  </Link>
                </div>
              )}
            </div>
            <button onClick={() => setDepositNotice(null)} className="text-lg leading-none" aria-label="Fermer">×</button>
          </div>
        )}

        {/* Contenu des étapes */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          {/* Étape 1: Livraison/Retrait */}
          {currentStep === 1 && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Livraison ou retrait en magasin ?
              </h2>

              {/* Choix delivery/pickup — masqué quand seule la reprise existe */}
              {!deliveryEnabled ? (
                <div className="p-6 rounded-xl border-2 border-green-600 bg-green-50 mb-8 text-center">
                  <div className="text-5xl mb-3">🏪</div>
                  <div className="text-2xl font-bold">Retrait en magasin</div>
                  <div className="text-gray-600 mt-2">
                    Gratuit — la livraison à domicile n'est pas encore proposée.
                  </div>
                </div>
              ) : (
              <div className="grid grid-cols-2 gap-4 mb-8">
                <button
                  onClick={() => setDeliveryOption('delivery')}
                  className={`p-6 rounded-xl border-2 transition-all ${
                    deliveryOption === 'delivery'
                      ? 'border-green-600 bg-green-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="text-5xl mb-3">🚚</div>
                  <div className="text-2xl font-bold">Livraison à domicile</div>
                  <div className="text-gray-600 mt-2">{libelleFraisLivraison()}</div>
                </button>

                <button
                  onClick={() => setDeliveryOption('pickup')}
                  className={`p-6 rounded-xl border-2 transition-all ${
                    deliveryOption === 'pickup'
                      ? 'border-green-600 bg-green-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="text-5xl mb-3">🏪</div>
                  <div className="text-2xl font-bold">Retrait en magasin</div>
                  <div className="text-gray-600 mt-2">Gratuit</div>
                </button>
              </div>
              )}

              {/* Adresses (si livraison) */}
              {deliveryOption === 'delivery' && (
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Adresse de livraison</h3>

                  {shopDelivery?.instructions && (
                    <p className="mb-4 text-gray-600">{shopDelivery.instructions}</p>
                  )}

                  {/* Le tarif réel de CETTE adresse, calculé par le serveur. */}
                  {selectedAddressId && deliveryQuoteLoading && (
                    <div className="mb-4 p-4 rounded-lg bg-gray-50 text-gray-600">
                      Calcul des frais de livraison…
                    </div>
                  )}

                  {selectedAddressId && !deliveryQuoteLoading && deliveryQuote?.deliverable && (
                    <div className="mb-4 p-4 rounded-lg border-2 border-green-200 bg-green-50">
                      <div className="font-bold text-green-800">
                        {deliveryQuote.free_applied
                          ? '🎁 Livraison offerte'
                          : `Frais de livraison : ${(deliveryQuote.fee_cents / 100).toFixed(2)} €`}
                      </div>
                      {deliveryQuote.zone_name && (
                        <div className="text-sm text-green-700 mt-1">
                          Zone : {deliveryQuote.zone_name}
                        </div>
                      )}
                      {!deliveryQuote.free_applied && deliveryQuote.free_above_cents ? (
                        <div className="text-sm text-green-700 mt-1">
                          Livraison offerte dès {(deliveryQuote.free_above_cents / 100).toFixed(2)} € d'achat.
                        </div>
                      ) : null}
                    </div>
                  )}

                  {selectedAddressId && !deliveryQuoteLoading && livraisonRefusee && (
                    <div className="mb-4 p-4 rounded-lg border-2 border-red-300 bg-red-50 text-red-800">
                      <div className="font-bold">Livraison impossible à cette adresse</div>
                      <div className="text-sm mt-1">{deliveryQuote?.reason}</div>
                      {deliveryQuote && deliveryQuote.missing_for_minimum_cents > 0 && (
                        <div className="text-sm mt-1">
                          Il manque {(deliveryQuote.missing_for_minimum_cents / 100).toFixed(2)} € à votre panier.
                        </div>
                      )}
                    </div>
                  )}

                  {addresses.length > 0 && !showAddressForm && (
                    <div className="space-y-3 mb-4">
                      {addresses.map(addr => (
                        <button
                          key={addr.id}
                          onClick={() => setSelectedAddressId(addr.id)}
                          className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                            selectedAddressId === addr.id
                              ? 'border-green-600 bg-green-50'
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          <div className="font-medium">{addr.street}</div>
                          <div className="text-gray-600">
                            {addr.postal_code} {addr.city}, {addr.country}
                          </div>
                          {addr.is_default && (
                            <span className="inline-block mt-2 px-2 py-1 bg-green-100 text-green-700 text-sm rounded">
                              Par défaut
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}

                  {!showAddressForm && (
                    <button
                      onClick={() => setShowAddressForm(true)}
                      className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-green-500 hover:text-green-700 transition-colors"
                    >
                      + Ajouter une nouvelle adresse
                    </button>
                  )}

                  {/* Formulaire nouvelle adresse */}
                  {showAddressForm && (
                    <div className="border-2 border-gray-300 rounded-lg p-6 space-y-4">
                      <h4 className="text-xl font-bold mb-4">Nouvelle adresse</h4>

                      <input
                        type="text"
                        placeholder="Rue et numéro"
                        value={newAddress.street}
                        onChange={e => setNewAddress({ ...newAddress, street: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-green-500"
                      />

                      <div className="grid grid-cols-2 gap-4">
                        <input
                          type="text"
                          placeholder="Code postal"
                          value={newAddress.postal_code}
                          onChange={e => setNewAddress({ ...newAddress, postal_code: e.target.value })}
                          className="px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-green-500"
                        />
                        <input
                          type="text"
                          placeholder="Ville"
                          value={newAddress.city}
                          onChange={e => setNewAddress({ ...newAddress, city: e.target.value })}
                          className="px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-green-500"
                        />
                      </div>

                      <div className="flex gap-3">
                        <button
                          onClick={handleCreateAddress}
                          disabled={loading || !newAddress.street || !newAddress.city || !newAddress.postal_code}
                          className="flex-1 bg-green-600 text-white py-3 rounded-lg font-bold hover:bg-green-700 disabled:bg-gray-300 transition-colors"
                        >
                          Enregistrer
                        </button>
                        <button
                          onClick={() => setShowAddressForm(false)}
                          className="px-6 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          Annuler
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Étape 2: Date et heure */}
          {currentStep === 2 && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Quand souhaitez-vous {deliveryOption === 'delivery' ? 'être livré' : 'récupérer votre commande'} ?
              </h2>

              {/* Ce que les précommandes imposent comme jours — dit avec les vrais
                  noms de produits, et l'intersection déjà faite par le serveur. */}
              {hasPreorderItems && pickupConstraints.length > 0 && (
                <div className="mb-6 p-4 bg-purple-50 border-2 border-purple-300 rounded-xl">
                  <div className="flex items-start gap-3">
                    <span className="text-3xl">🥖</span>
                    <div>
                      <h3 className="text-lg font-bold text-purple-900 mb-1">
                        Produits sur commande dans votre panier
                      </h3>
                      <p className="text-purple-700">
                        Ils sont fabriqués pour vous : le retrait n'est possible que certains jours.
                      </p>
                      <ul className="mt-2 text-sm text-purple-700 space-y-1">
                        {pickupConstraints.map(c => (
                          <li key={c.product_id}>
                            <span className="font-medium">{c.product_name}</span> :{' '}
                            {c.weekdays.map(d => JOURS_COURTS[d]).join(', ')}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* L'article qui repousse toute la commande, nommé avec sa raison.
                  Une commande n'a qu'une date : un pain de seigle cuit deux
                  vendredis par mois envoyait 33 articles à la semaine suivante
                  sans que le client sache pourquoi (M. Mattei, 07/10/2026). */}
              {pickupRetards.length > 0 && (
                <div className="mb-6 p-4 bg-amber-50 border-2 border-amber-300 rounded-xl space-y-3">
                  {pickupRetards.map((r) => (
                    <div key={r.product_id} className="flex items-start gap-3">
                      <span className="text-2xl" aria-hidden="true">⏳</span>
                      <div className="flex-1 text-amber-900">
                        <p>
                          <span className="font-bold">{r.product_name}</span>{' '}
                          {r.raison ? `${r.raison}.` : 'repousse la date de retrait.'}{' '}
                          {r.avec_lui
                            ? <>Avec lui, le premier retrait possible est le <b>{formatJourRetrait(r.avec_lui)}</b>.</>
                            : <>Avec lui, aucune date commune n'est possible.</>}{' '}
                          Sans lui, vous pouvez retirer votre commande dès le <b>{formatJourRetrait(r.sans_lui)}</b>.
                        </p>
                        <button
                          type="button"
                          onClick={() => removeItem(r.product_id)}
                          className="mt-2 px-3 py-1.5 text-sm font-semibold rounded-lg border-2 border-amber-400 bg-white text-amber-900 active:bg-amber-100"
                        >
                          Retirer cet article du panier
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Sélection date */}
              <div className="mb-6">
                <label className="block text-xl font-bold text-gray-900 mb-3">Date</label>

                {pickupLoading ? (
                  <div className="p-6 bg-gray-50 border-2 border-gray-200 rounded-lg text-center text-gray-600">
                    Recherche des dates disponibles…
                  </div>
                ) : availableDates.length > 0 ? (
                  <div className="grid grid-cols-2 gap-3">
                    {availableDates.slice(0, 8).map(dateStr => {
                      const date = new Date(dateStr + 'T12:00:00');
                      const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
                      const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
                      const dayName = dayNames[date.getDay()];
                      const day = date.getDate();
                      const month = monthNames[date.getMonth()];

                      return (
                        <button
                          key={dateStr}
                          onClick={() => setDeliveryDate(dateStr)}
                          className={`p-4 rounded-lg border-2 text-left transition-all ${
                            deliveryDate === dateStr
                              ? 'border-green-600 bg-green-50'
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          <div className="text-sm text-gray-600">{dayName}</div>
                          <div className="text-xl font-bold">{day} {month}</div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-6 bg-red-50 border-2 border-red-300 rounded-lg">
                    <p className="text-red-700 font-bold mb-1">
                      Aucune date de retrait possible pour ce panier
                    </p>
                    <p className="text-red-700">
                      {pickupConstraints.length > 1
                        ? 'Les articles sur commande de votre panier ne sont pas disponibles les mêmes jours. Passez deux commandes séparées, ou retirez l’un d’eux.'
                        : 'Réessayez plus tard, ou retirez de votre panier les articles fabriqués sur commande.'}
                    </p>
                  </div>
                )}
              </div>

              {/* Sélection créneau — uniquement si la boutique en publie */}
              {timeSlots.length === 0 ? (
                <div className="p-5 bg-[#f0e9d8] rounded-xl text-[#4a4530]">
                  <span className="font-bold">Pas d'heure à choisir.</span> Vous recevrez un
                  message dès que votre commande est prête : venez la chercher quand cela
                  vous arrange, le jour choisi.
                </div>
              ) : (
              <div>
                <label className="block text-xl font-bold text-gray-900 mb-3">Créneau horaire</label>
                <div className="grid grid-cols-2 gap-3">
                  {timeSlots.map(slot => (
                    <button
                      key={slot}
                      onClick={() => setDeliverySlot(slot)}
                      className={`p-4 rounded-lg border-2 text-lg font-medium transition-all ${
                        deliverySlot === slot
                          ? 'border-green-600 bg-green-50 text-green-700'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
              </div>
              )}
            </div>
          )}

          {/* Étape 3: Paiement */}
          {currentStep === 3 && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Mode de paiement</h2>

              {minOrderCents > 0 && totals.total_ttc_cents < minOrderCents && (
                <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800">
                  Montant minimum de commande : <strong>{(minOrderCents / 100).toFixed(2)} €</strong> — il manque{' '}
                  {((minOrderCents - totals.total_ttc_cents) / 100).toFixed(2)} € à votre panier.
                </div>
              )}

              <div className="space-y-4">
                {/* Wallet */}
                <button
                  onClick={() => setPaymentMethod('wallet')}
                  className={`w-full p-6 rounded-xl border-2 text-left transition-all ${
                    paymentMethod === 'wallet'
                      ? 'border-green-600 bg-green-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-2xl font-bold mb-1">💳 Mes FOLIES</div>
                      <div className="text-gray-600">
                        Solde disponible:{' '}
                        <span className="font-bold text-green-700">
                          {wallet ? (wallet.balance_cents / 100).toFixed(2) : '0.00'} €
                        </span>
                      </div>
                      {wallet && wallet.balance_cents < totalAvecLivraisonCents && (
                        <div className="text-red-600 font-medium mt-1">
                          ⚠️ Solde insuffisant — il manque{' '}
                          {((totalAvecLivraisonCents - wallet.balance_cents) / 100).toFixed(2)} €
                        </div>
                      )}
                    </div>
                  </div>
                </button>

                {/* Compléter le solde : recharge Stripe du manquant, puis retour ici en FOLIES */}
                {paymentMethod === 'wallet' && wallet && wallet.balance_cents < totalAvecLivraisonCents && (
                  <div className="rounded-xl border-2 border-dashed border-green-600 bg-green-50 p-5">
                    <p className="text-gray-800 mb-3">
                      Vos <strong>{(wallet.balance_cents / 100).toFixed(2)} €</strong> de FOLIES ne
                      couvrent pas ce panier. Complétez votre solde par carte, vous reviendrez ici pour
                      payer en FOLIES — vos FOLIES sont bien utilisées.
                    </p>
                    <button
                      type="button"
                      onClick={handleTopUpMissing}
                      disabled={topUpLoading || brouillonEnCours || !cgvAccepted}
                      className="w-full sm:w-auto px-6 py-3 rounded-xl bg-green-700 text-white font-bold hover:bg-green-800 disabled:opacity-50"
                    >
                      {topUpLoading
                        ? 'Redirection vers le paiement…'
                        : `💳 Recharger ${(topUpCents / 100).toFixed(2)} € par carte`}
                    </button>
                    {/* La commande sera enregistrée dès le paiement reçu, sans
                        repasser par cet écran : les CGV doivent donc être
                        acceptées AVANT le départ, pas au retour. */}
                    {!cgvAccepted && (
                      <p className="text-sm text-gray-700 mt-2">
                        Cochez d'abord l'acceptation des conditions générales, plus bas sur cette page.
                      </p>
                    )}
                    <p className="text-sm text-gray-700 mt-2">
                      Votre commande est enregistrée dès le paiement reçu — vous n'avez rien à
                      refaire au retour.
                    </p>
                    <p className="text-xs text-gray-600 mt-2">
                      {topUpCents > missingCents
                        ? `Il vous manque ${(missingCents / 100).toFixed(2)} € ; la recharge minimum est de 1 €, le surplus reste sur vos FOLIES.`
                        : 'Exactement le montant manquant : après la recharge, votre panier sera couvert en FOLIES.'}
                    </p>
                  </div>
                )}

                {/* Carte bancaire */}
                <button
                  onClick={() => setPaymentMethod('card')}
                  className={`w-full p-6 rounded-xl border-2 text-left transition-all ${
                    paymentMethod === 'card'
                      ? 'border-green-600 bg-green-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="text-2xl font-bold mb-1">💳 Carte bancaire</div>
                  <div className="text-gray-600">Paiement sécurisé en ligne</div>
                </button>

                {/* Pas de paiement sur place pour les autres : une commande
                    n'est préparée qu'une fois payée (CGV art. 5 et 6). Sans
                    prépaiement, une commande non retirée immobilise de la
                    marchandise fraîche déjà prélevée du rayon. */}
                {auRetraitAutorise && (
                  <button
                    onClick={() => setPaymentMethod('cash')}
                    className={`w-full p-6 rounded-xl border-2 text-left transition-all ${
                      paymentMethod === 'cash'
                        ? 'border-green-600 bg-green-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div className="text-2xl font-bold mb-1">🧾 Payer au retrait</div>
                    <div className="text-gray-600">
                      Au comptoir : espèces, chèques repas ou Bancontact. Le montant exact est celui de la
                      commande préparée (les produits au poids sont pesés).
                    </div>
                  </button>
                )}
              </div>

              <p className="mt-3 text-sm text-gray-600">
                {paymentMethod === 'cash'
                  ? 'Votre commande est confirmée tout de suite ; vous la réglez en venant la chercher. Les chèques repas ne paient que l\'alimentaire.'
                  : auRetraitAutorise
                    ? 'La commande est confirmée une fois le paiement reçu.'
                    : 'La commande est confirmée une fois le paiement reçu. Aucun paiement au retrait.'}
              </p>

              {/* Code promo */}
              <div className="mt-6 pt-6 border-t-2 border-gray-200">
                <h3 className="text-xl font-bold mb-3">Code promo</h3>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={promoCode}
                    onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoError(''); }}
                    placeholder="Entrez votre code"
                    className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-green-500 uppercase"
                  />
                  <button
                    onClick={handleApplyPromo}
                    disabled={promoLoading || !promoCode.trim()}
                    className="px-6 py-3 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 disabled:bg-gray-300 transition-colors"
                  >
                    {promoLoading ? '...' : 'Appliquer'}
                  </button>
                </div>
                {promoError && <p className="text-red-600 text-sm mt-2">{promoError}</p>}
                {promoDiscount && (
                  <div className="mt-3 p-3 bg-green-50 border border-green-300 rounded-lg">
                    <p className="text-green-700 font-medium">
                      Réduction appliquée : -{(promoDiscount.discount_cents / 100).toFixed(2)} €
                      {promoDiscount.description && <span className="text-sm text-green-700 block">{promoDiscount.description}</span>}
                    </p>
                  </div>
                )}
              </div>

              {/* Vidanges rapportées au retrait (crédit FOLIES après recomptage) */}
              {deliveryOption !== 'delivery' && <VidangesAnnonce value={vidanges} onChange={setVidanges} />}

              {/* Bacs Folies vides rapportés : échangés un pour un au retrait */}
              {deliveryOption !== 'delivery' && <BacsAnnonce value={bacs} onChange={setBacs} />}

              {/* Commentaire client */}
              <div className="mt-8 border-t-2 border-gray-200 pt-6">
                <h3 className="text-2xl font-bold mb-3">Commentaire (optionnel)</h3>
                <p className="text-gray-600 mb-3">
                  {deliveryOption === 'delivery'
                    ? 'Ajoutez des instructions particulières pour votre commande ou votre livraison.'
                    : 'Une préférence, une précision sur un article ? Dites-le-nous ici.'}
                </p>
                <textarea
                  value={customerComment}
                  onChange={e => setCustomerComment(e.target.value)}
                  placeholder={
                    deliveryOption === 'delivery'
                      ? 'Ex : livrer après 18h, sonner chez le voisin si absent…'
                      : 'Ex : des carottes plutôt petites, un pain bien cuit…'
                  }
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-green-500 resize-none"
                />
                <div className="text-right text-sm text-gray-600 mt-1">
                  {customerComment.length}/500 caractères
                </div>
              </div>

              {/* Appel personnalisé — masqué tant que la ligne n'est pas ouverte */}
              {voiceAssistEnabled && (
              <div className="mt-8 border-t-2 border-gray-200 pt-6">
                <label className="flex items-start gap-4 cursor-pointer group">
                  <div className="flex-shrink-0 mt-1">
                    <input
                      type="checkbox"
                      checked={callRequested}
                      onChange={e => setCallRequested(e.target.checked)}
                      className="w-6 h-6 text-green-700 border-2 border-gray-300 rounded focus:ring-green-500 cursor-pointer"
                    />
                  </div>
                  <div>
                    <div className="text-xl font-bold text-gray-900 group-hover:text-green-700 transition-colors">
                      Je souhaite un appel pendant la préparation
                    </div>
                    <p className="text-gray-600 mt-1">
                      Un préparateur vous appellera pour discuter des substitutions possibles,
                      suggestions du jour et vos préférences. C'est gratuit et sans engagement.
                    </p>
                    {callRequested && !customer?.phone && (
                      <p className="text-amber-600 font-medium mt-2">
                        Pensez à renseigner votre numéro de téléphone dans votre profil pour recevoir l'appel.
                      </p>
                    )}
                  </div>
                </label>
              </div>
              )}

              {/* Acceptation CGV (obligatoire) */}
              <div className="mt-8 border-t-2 border-gray-200 pt-6">
                <label className="flex items-start gap-4 cursor-pointer group">
                  <div className="flex-shrink-0 mt-1">
                    <input
                      type="checkbox"
                      checked={cgvAccepted}
                      onChange={e => setCgvAccepted(e.target.checked)}
                      className="w-6 h-6 text-green-700 border-2 border-gray-300 rounded focus:ring-green-500 cursor-pointer"
                    />
                  </div>
                  <div>
                    <div className="text-base text-gray-900 group-hover:text-green-700 transition-colors">
                      J'ai lu et j'accepte les{' '}
                      <Link to="/shop/cgv" target="_blank" className="text-green-700 hover:underline font-medium">
                        Conditions Generales de Vente
                      </Link>{' '}
                      et la{' '}
                      <Link to="/shop/privacy" target="_blank" className="text-green-700 hover:underline font-medium">
                        Politique de confidentialite
                      </Link>
                      {' *'}
                    </div>
                  </div>
                </label>
                {!cgvAccepted && error?.includes('CGV') && (
                  <p className="text-red-600 text-sm mt-2">Vous devez accepter les CGV pour continuer.</p>
                )}
              </div>

              {/* Récapitulatif */}
              <div className="mt-8 border-t-2 border-gray-200 pt-6">
                <h3 className="text-2xl font-bold mb-4">Récapitulatif</h3>

                <div className="space-y-2 text-lg">
                  <div className="flex justify-between">
                    <span>Mode:</span>
                    <span className="font-medium">
                      {deliveryOption === 'delivery' ? '🚚 Livraison' : '🏪 Retrait magasin'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Date:</span>
                    <span className="font-medium">{deliveryDate || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{timeSlots.length > 0 ? 'Créneau:' : 'Heure:'}</span>
                    <span className="font-medium">
                      {timeSlots.length > 0
                        ? deliverySlot || '-'
                        : 'quand vous voulez, après notre message'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Articles:</span>
                    <span className="font-medium">{totals.item_count}</span>
                  </div>
                  {totals.deposit_lines.map(l => (
                    <div key={l.name} className="flex justify-between">
                      <span>♻️ {l.name} × {l.quantity}:</span>
                      <span className="font-medium">{(l.total_cents / 100).toFixed(2)} €</span>
                    </div>
                  ))}
                  {totals.deposit_total_cents > 0 && (
                    <p className="text-xs text-gray-500">
                      La consigne vous est remboursée quand vous rapportez le contenant vide au magasin.
                    </p>
                  )}
                  {deliveryOption === 'delivery' && deliveryQuote?.deliverable && (
                    <div className="flex justify-between">
                      <span>Frais de livraison:</span>
                      <span className="font-medium">
                        {deliveryQuote.free_applied
                          ? 'offerts'
                          : `${(deliveryQuote.fee_cents / 100).toFixed(2)} €`}
                      </span>
                    </div>
                  )}
                  {promoDiscount && (
                    <div className="flex justify-between text-green-700">
                      <span>Réduction code promo:</span>
                      <span className="font-medium">-{(promoDiscount.discount_cents / 100).toFixed(2)} €</span>
                    </div>
                  )}
                  <div className="flex justify-between text-2xl font-bold text-green-700 mt-4 pt-4 border-t-2">
                    <span>Total:</span>
                    <span>{(
                      (totalAvecLivraisonCents - (promoDiscount?.discount_cents ?? 0)) / 100
                    ).toFixed(2)} €</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Boutons navigation */}
          <div className="flex gap-4 mt-8">
            {currentStep > 1 && (
              <button
                onClick={handlePreviousStep}
                className="px-8 py-4 border-2 border-gray-300 rounded-xl font-bold text-xl hover:bg-gray-50 transition-colors"
              >
                ← Retour
              </button>
            )}

            {currentStep < 3 ? (
              <button
                onClick={handleNextStep}
                className="flex-1 bg-green-600 text-white py-4 rounded-xl font-bold text-xl hover:bg-green-700 transition-colors"
              >
                Continuer →
              </button>
            ) : (
              <button
                onClick={handleConfirmOrder}
                disabled={
                  loading ||
                  brouillonEnCours ||
                  (currentStep === 3 && !cgvAccepted) ||
                  articlesBloques.length > 0 ||
                  livraisonRefusee ||
                  devisLivraisonManquant ||
                  (minOrderCents > 0 && totals.total_ttc_cents < minOrderCents)
                }
                className="flex-1 bg-gradient-to-r from-green-600 to-green-700 text-white py-4 rounded-xl font-bold text-xl hover:from-green-700 hover:to-green-800 disabled:from-gray-400 disabled:to-gray-400 transition-all"
              >
                {brouillonEnCours
                  ? 'Enregistrement de votre commande…'
                  : loading
                    ? 'Confirmation en cours...'
                    : '✓ Confirmer la commande'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
